import React from 'react';
import { Row, Col, Card, Statistic, Descriptions, Alert, Spin, Empty, Tag } from 'antd';
import { ThunderboltOutlined, DollarOutlined, DashboardOutlined } from '@ant-design/icons';
import { useCapacityFee } from '@/api/capacityFee';

interface CapacityFeeTabProps {
  station: string;
  month: string; // YYYYMM
}

const CapacityFeeTab: React.FC<CapacityFeeTabProps> = ({ station, month }) => {
  const params = station && month ? { station, month } : null;
  const { data, isLoading, error } = useCapacityFee(params);
  const d = data?.data;

  const peakLabel = (hours: number[]) => {
    if (!hours || hours.length === 0) return '-';
    const start = hours[0] - 1;
    const end = hours[hours.length - 1];
    return `${String(start).padStart(2, '0')}:00-${String(end).padStart(2, '0')}:00`;
  };

  const monthDisplay = month ? `${month.slice(0, 4)}-${month.slice(4, 6)}` : '';

  if (!params) return <Empty description="请选择场站和日期" />;
  if (isLoading) return <Spin tip="计算中..." style={{ display: 'block', marginTop: 80 }}><div style={{ height: 200 }} /></Spin>;
  if (error) return <Alert type="error" message="加载失败" description={(error as Error).message} showIcon />;
  if (!d) return <Empty description={`${station} ${monthDisplay} 暂无数据`} />;

  return (
    <div>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="容量电费"
              value={d.capacity_fee_yuan}
              precision={0}
              prefix={<DollarOutlined />}
              suffix="元"
              valueStyle={{ color: '#cf1322', fontSize: 24 }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="容量"
              value={d.capacity_mw}
              precision={2}
              prefix={<DashboardOutlined />}
              suffix="MW"
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="尖峰时段总电量"
              value={d.total_energy_mwh}
              precision={0}
              prefix={<ThunderboltOutlined />}
              suffix="MWh"
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="尖峰时段"
              value={`${d.peak_count_per_day}h/天`}
              suffix={peakLabel(d.peak_table_hours)}
            />
          </Card>
        </Col>
      </Row>

      <Card title={`${station} ${monthDisplay} 容量电费明细`} size="small" style={{ marginBottom: 16 }}>
        <Descriptions column={4} size="small">
          <Descriptions.Item label="当月天数">{d.days_in_month}天</Descriptions.Item>
          <Descriptions.Item label="尖峰时刻">
            {d.peak_table_hours?.join(', ')} ({peakLabel(d.peak_table_hours)})
          </Descriptions.Item>
          <Descriptions.Item label="日清分数据">
            {d.settle_energy_mwh.toFixed(2)} MWh
            <Tag color="blue" style={{ marginLeft: 8 }}>{d.settle_days}天</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="AGC补齐">
            {d.agc_fill_energy_mwh.toFixed(2)} MWh
            <Tag color="orange" style={{ marginLeft: 8 }}>{d.agc_fill_days}天</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="总电量">{d.total_energy_mwh.toFixed(2)} MWh</Descriptions.Item>
          <Descriptions.Item label="月均每小时">
            {d.total_energy_mwh.toFixed(2)} / ({d.days_in_month}×{d.peak_count_per_day}) = {d.avg_mw_per_hour.toFixed(4)} MW
          </Descriptions.Item>
          <Descriptions.Item label="容量">
            {d.avg_mw_per_hour.toFixed(4)} / 5 = {d.capacity_mw.toFixed(4)} MW
          </Descriptions.Item>
          <Descriptions.Item label="容量电费">
            {d.capacity_mw.toFixed(4)} × 20,000 = {d.capacity_fee_yuan.toFixed(2)} 元
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {d.verification && d.verification.units.length > 0 && (
        <Card title="月清分验证" size="small">
          {d.verification.units.map((u, i) => (
            <Descriptions key={i} column={2} size="small" bordered style={{ marginBottom: 8 }}>
              <Descriptions.Item label="单元">{u.unit}</Descriptions.Item>
              <Descriptions.Item label="可用容量">{u.capacity_mw} MW</Descriptions.Item>
              <Descriptions.Item label="容量补偿费用">{u.compensation_fee.toLocaleString()} 元</Descriptions.Item>
            </Descriptions>
          ))}
          <Descriptions column={4} size="small" style={{ marginTop: 8 }}>
            <Descriptions.Item label="月清分容量合计">{d.verification.total_capacity_mw} MW</Descriptions.Item>
            <Descriptions.Item label="月清分补偿合计">{d.verification.total_compensation_fee.toLocaleString()} 元</Descriptions.Item>
            <Descriptions.Item label="计算容量">
              {d.capacity_mw.toFixed(4)} MW
              {d.verification.total_capacity_mw > 0 && (
                <Tag color={Math.abs(d.capacity_mw - d.verification.total_capacity_mw) / d.verification.total_capacity_mw < 0.02 ? 'green' : 'orange'} style={{ marginLeft: 4 }}>
                  {(Math.abs(d.capacity_mw - d.verification.total_capacity_mw) / d.verification.total_capacity_mw * 100).toFixed(2)}%
                </Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="计算电费">
              {d.capacity_fee_yuan.toLocaleString()} 元
            </Descriptions.Item>
          </Descriptions>
        </Card>
      )}

      {d.agc_fill_days > 0 && (
        <Alert
          type="info"
          showIcon
          style={{ marginTop: 12 }}
          message={`日清分缺 ${d.agc_fill_days} 天数据，用 AGC 补齐（AGC 实测偏差约 1.9%）`}
        />
      )}
    </div>
  );
};

export default CapacityFeeTab;
